import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiCalendar,
  FiClock,
  FiMapPin,
  FiGlobe,
  FiUsers,
  FiStar,
  FiArrowRight,
  FiCheck,
  FiLoader,
  FiLock,
  FiAlertCircle,
  FiTag,
  FiBookOpen,
  FiDollarSign,
  FiChevronDown,
  FiChevronUp,
  FiEye,
} from "react-icons/fi";
import {
  getWorkshopStatus,
  canUserJoin,
  formatPrice,
} from "../../../utils/workshopUtils";
import "./WSPWorkshopCard.css";

// ========================================
// Helper: Get banner color class from category
// ========================================
const getBannerClass = (category) => {
  if (!category) return "banner-default";
  const cat = category.toLowerCase();
  if (cat.includes("software") || cat.includes("tech") || cat.includes("web") || cat.includes("programming") || cat.includes("computer") || cat.includes("data"))
    return "banner-tech";
  if (cat.includes("science") || cat.includes("research") || cat.includes("math") || cat.includes("physics") || cat.includes("quantum"))
    return "banner-science";
  if (cat.includes("business") || cat.includes("management") || cat.includes("finance") || cat.includes("marketing"))
    return "banner-business";
  if (cat.includes("art") || cat.includes("design") || cat.includes("creative") || cat.includes("music"))
    return "banner-arts";
  if (cat.includes("health") || cat.includes("medical") || cat.includes("bio"))
    return "banner-health";
  return "banner-default";
};

/**
 * WSPWorkshopCard - Premium Redesigned Workshop Card
 * Clean, elegant, inspired by the interior detail page aesthetics
 */
const WSPWorkshopCard = ({
  workshop,
  onApply,
  currentUser,
  viewMode = "grid",
  joiningWorkshopId = null,
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const isJoining = joiningWorkshopId === workshop._id;

  const DESC_LIMIT = viewMode === "list" ? 180 : 100;
  const TITLE_LIMIT = 60;

  const needsExpansion = useMemo(() => {
    return (workshop.title?.length > TITLE_LIMIT) || (workshop.description?.length > DESC_LIMIT);
  }, [workshop.title, workshop.description, TITLE_LIMIT, DESC_LIMIT]);

  // ========================================
  // MEMOIZED COMPUTATIONS
  // ========================================
  const workshopMeta = useMemo(() => {
    const status = getWorkshopStatus(workshop);
    const joinResult = canUserJoin(workshop, currentUser);
    const canJoinValue = joinResult.canJoin;
    const reason = joinResult.reason;

    const now = new Date();
    const startDateValue = workshop.duration?.startDate ? new Date(workshop.duration.startDate) : null;
    const endDateValue = workshop.duration?.endDate ? new Date(workshop.duration.endDate) : null;

    let daysUntil = 0;
    let hasStarted = false;

    if (startDateValue) {
      const diffTime = startDateValue - now;
      daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      hasStarted = now >= startDateValue;
    }

    const capacityLimit = workshop.capacity?.limit || 0;
    const isUnlimited = workshop.capacity?.unlimited;
    const participantCount = workshop.participantCount || 0;
    const isFull = !isUnlimited && participantCount >= capacityLimit;

    const currentUserId = currentUser && (currentUser._id || currentUser.id);
    const organizerId = workshop.organizer && (workshop.organizer._id || workshop.organizer.id);
    const isOrganizer = currentUserId && organizerId && (currentUserId === organizerId || currentUserId.toString() === organizerId.toString());

    const isCoOrganizer = currentUserId && workshop.coOrganizers?.some((co) => {
      const coUserId = typeof co.user === "string" ? co.user : (co.user?._id || co.user?.id);
      const altUserId = co.userId || co._id;
      const idToCheck = coUserId || altUserId;
      return idToCheck && (idToCheck === currentUserId || idToCheck.toString() === currentUserId.toString());
    });

    const durationDays = workshop.duration?.days || 1;

    return { status, canJoin: canJoinValue, reason, daysUntil, isFull, hasStarted, isOrganizer, isCoOrganizer, startDate: startDateValue, endDate: endDateValue, durationDays };
  }, [workshop, currentUser]);

  const formattedDates = useMemo(() => {
    const start = workshopMeta.startDate;
    const end = workshopMeta.endDate;
    if (!start) return { dateRange: "TBA", startTime: "" };

    const options = { month: "short", day: "numeric" };
    const startFormatted = start.toLocaleDateString("en-US", options);
    let dateRange = startFormatted;
    if (end && start.getTime() !== end.getTime()) {
      dateRange = `${startFormatted} - ${end.toLocaleDateString("en-US", options)}`;
    }
    return { dateRange };
  }, [workshopMeta.startDate, workshopMeta.endDate]);

  // ========================================
  // EVENT HANDLERS
  // ========================================
  const handleCardClick = useCallback((e) => {
    if (e.target.closest("button")) return;
    navigate("/workshops/" + workshop._id);
  }, [workshop._id, navigate]);

  const handleApply = useCallback((e) => {
    e.stopPropagation();
    if (onApply) {
      const isPaid = workshop.pricing?.type === "paid";
      onApply(workshop._id, isPaid);
    }
  }, [workshop._id, workshop.pricing, onApply]);

  const handleViewDetails = useCallback((e) => {
    e.stopPropagation();
    navigate("/workshops/" + workshop._id);
  }, [workshop._id, navigate]);

  const handleOrganizerClick = useCallback((e) => {
    e.stopPropagation();
    const organizerId = workshop.organizer?._id || workshop.organizer?.id;
    if (organizerId) navigate(`/profile/${organizerId}`);
  }, [workshop.organizer, navigate]);

  // ========================================
  // HELPERS
  // ========================================
  const getOrganizerName = () => {
    if (!workshop.organizer) return "Unknown Organizer";
    return workshop.organizer.fullName || workshop.organizer.name || workshop.organizer.username || "Unknown";
  };

  const getOrganizerInitial = () => getOrganizerName().charAt(0).toUpperCase();

  const getDescription = () => {
    if (!workshop.description) return null;
    if (isExpanded) return workshop.description;
    if (workshop.description.length > DESC_LIMIT) return workshop.description.substring(0, DESC_LIMIT) + "...";
    return workshop.description;
  };

  const getLocationText = () => {
    if (workshop.format === "online") return workshop.location?.platform || "Online";
    return workshop.location?.city || workshop.location?.venue || "In-Person";
  };

  const getPriceDisplay = () => {
    if (!workshop.pricing || workshop.pricing.type === "free") return "Free";
    return formatPrice(workshop.pricing.amount);
  };

  const getCapacityDisplay = () => {
    const count = workshop.participantCount || 0;
    if (workshop.capacity?.unlimited) return `${count} enrolled`;
    const limit = workshop.capacity?.limit || 0;
    return `${count}/${limit}`;
  };

  const getStatusBadge = () => {
    const status = workshopMeta.status;
    if (status === "ongoing") return { text: "Live Now", class: "wsp-badge-live" };
    if (status === "completed") return { text: "Completed", class: "wsp-badge-completed" };
    if (status === "cancelled") return { text: "Cancelled", class: "wsp-badge-cancelled" };
    return null;
  };

  // ========================================
  // RENDER HELPERS
  // ========================================
  const renderParticipantPreview = () => {
    const { participantPreview = [], participantCount = 0 } = workshop;
    const showLimit = 4;
    const remaining = participantCount - showLimit;

    if (participantCount === 0) {
      return (
        <div className="wsp-participant-preview">
          <span className="wsp-participant-cta">Be the first to join!</span>
        </div>
      );
    }

    return (
      <div className="wsp-participant-preview">
        <div className="wsp-participant-avatars">
          {participantPreview.slice(0, showLimit).map((user) => (
            <div key={user._id} className="wsp-participant-avatar" title={user.name}>
              {user.profilePhotoUrl ? (
                <img src={user.profilePhotoUrl} alt={user.name} loading="lazy" />
              ) : (
                <div className="wsp-participant-placeholder">
                  {user.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
              )}
            </div>
          ))}
          {remaining > 0 && <div className="wsp-participant-more">+{remaining}</div>}
        </div>
      </div>
    );
  };

  const renderBadges = () => {
    const badges = [];
    const statusBadge = getStatusBadge();

    if (statusBadge) {
      badges.push(<span key="status" className={`wsp-card-badge ${statusBadge.class}`}>{statusBadge.text}</span>);
    }

    if ((workshop.featured || workshop.isFeatured) && !statusBadge) {
      badges.push(<span key="featured" className="wsp-card-badge wsp-badge-featured"><FiStar size={10} /> Featured</span>);
    }

    if (workshop.certificateOffered) {
      badges.push(<span key="certificate" className="wsp-card-badge wsp-badge-certificate">🏆 Certificate</span>);
    }

    if (workshopMeta.daysUntil > 0 && workshopMeta.daysUntil <= 3 && !statusBadge) {
      const daysText = workshopMeta.daysUntil === 1 ? "Tomorrow" : `${workshopMeta.daysUntil} days`;
      badges.push(<span key="soon" className="wsp-card-badge wsp-badge-soon"><FiClock size={10} /> {daysText}</span>);
    }

    return badges;
  };

  const renderActionButton = () => {
    const participantStatus = workshop.userRole?.participantStatus || workshop.participantStatus;
    const isParticipant = workshop.userRole?.isParticipant || participantStatus != null;
    const isCoOrganizer = workshopMeta.isCoOrganizer || workshop.userRole?.isCoOrganizer || workshop.isCoOrganizer;

    if (workshopMeta.isOrganizer || workshop.userRole?.isOrganizer) {
      return (<button className="wsp-card-action wsp-action-manage" onClick={handleViewDetails}><span>Manage</span><FiArrowRight size={14} /></button>);
    }

    if (isCoOrganizer) {
      return (<button className="wsp-card-action wsp-action-co-manage" onClick={handleViewDetails}><span>Co-Manage</span><FiArrowRight size={14} /></button>);
    }

    if (participantStatus === "approved" || (isParticipant && participantStatus === "approved")) {
      return (<button className="wsp-card-action wsp-action-enrolled" onClick={handleViewDetails}><FiCheck size={14} /><span>Enter Workshop</span></button>);
    }

    if (participantStatus === "pending") {
      return (<button className="wsp-card-action wsp-action-pending" disabled><FiLoader size={14} className="wsp-spin" /><span>Pending</span></button>);
    }

    if (participantStatus === "payment_pending") {
      return (<button className="wsp-card-action wsp-action-payment" onClick={(e) => { e.stopPropagation(); navigate("/workshops/" + workshop._id + "/payment"); }}><FiDollarSign size={14} /><span>Pay Now</span></button>);
    }

    if (workshopMeta.isFull) {
      return (<button className="wsp-card-action wsp-action-full" disabled><FiLock size={14} /><span>Full</span></button>);
    }

    if (workshopMeta.hasStarted) {
      return (<button className="wsp-card-action wsp-action-started" disabled><FiAlertCircle size={14} /><span>Started</span></button>);
    }

    const isPaid = workshop.pricing?.type === "paid";
    const buttonText = isPaid ? "Apply" : "Join Free";

    if (isJoining) {
      return (<button className="wsp-card-action wsp-action-apply wsp-action-loading" disabled><FiLoader size={14} className="wsp-spinner" /><span>Joining...</span></button>);
    }

    return (<button className="wsp-card-action wsp-action-apply" onClick={handleApply}><span>{buttonText}</span><FiArrowRight size={14} /></button>);
  };

  // ========================================
  // RENDER
  // ========================================
  const isListView = viewMode === "list";
  const cardClassName = `wsp-card ${isListView ? "wsp-card-list" : ""} ${isExpanded ? "wsp-card-expanded" : ""}`;
  const badges = renderBadges();
  const isFree = !workshop.pricing || workshop.pricing.type === "free";
  const bannerClass = getBannerClass(workshop.category || workshop.field);

  const displayTitle = useMemo(() => {
    if (!workshop.title) return "Untitled Workshop";
    if (isExpanded) return workshop.title;
    if (workshop.title.length > TITLE_LIMIT) return workshop.title.substring(0, TITLE_LIMIT) + "...";
    return workshop.title;
  }, [workshop.title, isExpanded, TITLE_LIMIT]);

  return (
    <motion.article
      className={cardClassName}
      onClick={handleCardClick}
    >
      {/* Color Banner */}
      <div className={`wsp-card-banner ${bannerClass}`} />

      {/* Card Body */}
      <div className="wsp-card-body">
        {/* Top Row: Badges + Price */}
        <div className="wsp-card-top-row">
          <div className="wsp-card-badges">
            {workshop.category && (
              <span className="wsp-card-category-chip">
                <FiTag size={11} />
                {workshop.category}
              </span>
            )}
            {badges}
          </div>
          <span className={`wsp-card-price-tag ${isFree ? "wsp-price-free" : ""}`}>
            {getPriceDisplay()}
          </span>
        </div>

        {/* Title */}
        <h3 className="wsp-card-title">{displayTitle}</h3>

        {/* Description */}
        {getDescription() && (
          <p className="wsp-card-description">{getDescription()}</p>
        )}

        {/* Expand Toggle */}
        {needsExpansion && !isListView && (
          <button className="wsp-card-expand-toggle" onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}>
            {isExpanded ? (<><span>See less</span><FiChevronUp size={13} /></>) : (<><span>See more</span><FiChevronDown size={13} /></>)}
          </button>
        )}

        {/* Meta Strip - Date, Location, Duration, Capacity */}
        <div className="wsp-card-meta-strip">
          <span className="wsp-meta-chip"><FiCalendar size={13} />{formattedDates.dateRange}</span>
          <span className="wsp-meta-divider" />
          <span className="wsp-meta-chip">
            {workshop.format === "online" ? <FiGlobe size={13} /> : <FiMapPin size={13} />}
            {getLocationText()}
          </span>
          <span className="wsp-meta-divider" />
          <span className="wsp-meta-chip"><FiClock size={13} />{workshopMeta.durationDays} {workshopMeta.durationDays === 1 ? "day" : "days"}</span>
          <span className="wsp-meta-divider" />
          <span className="wsp-meta-chip"><FiUsers size={13} />{getCapacityDisplay()}</span>
          {/* Rating */}
          {workshop.averageRating > 0 && (
            <>
              <span className="wsp-meta-divider" />
              <span className="wsp-rating-chip">
                <FiStar size={12} />
                {workshop.averageRating.toFixed(1)}
                {workshop.feedbackCount > 0 && <span className="wsp-rating-count">({workshop.feedbackCount})</span>}
              </span>
            </>
          )}
        </div>

        {/* Tags Row - Field, Level, Language */}
        {(workshop.field || workshop.difficultyLevel || workshop.language) && (
          <div className="wsp-card-tags-row">
            {workshop.field && (
              <span className="wsp-tag-chip wsp-tag-field"><FiBookOpen size={10} />{workshop.field}</span>
            )}
            {workshop.difficultyLevel && (
              <span className={`wsp-tag-chip wsp-tag-level level-${workshop.difficultyLevel}`}>
                {workshop.difficultyLevel === "all-levels" ? "All Levels" : workshop.difficultyLevel.charAt(0).toUpperCase() + workshop.difficultyLevel.slice(1)}
              </span>
            )}
            {workshop.language && (
              <span className="wsp-tag-chip wsp-tag-language">🌐 {workshop.language}</span>
            )}
          </div>
        )}

        {/* People Row: Organizer + Participants */}
        <div className="wsp-card-people-row">
          <div className="wsp-card-organizer" onClick={handleOrganizerClick}>
            <div className="wsp-organizer-avatar">
              {workshop.organizer?.profilePhotoUrl ? (
                <img src={workshop.organizer.profilePhotoUrl} alt={getOrganizerName()} loading="lazy" />
              ) : (
                <span className="wsp-avatar-placeholder">{getOrganizerInitial()}</span>
              )}
            </div>
            <div className="wsp-organizer-info">
              <div className="wsp-organizer-name-row">
                <span className="wsp-organizer-name">{getOrganizerName()}</span>
                {workshop.organizer?.isVerified && (
                  <span className="wsp-verified-badge" title="Verified"><FiCheck size={8} /></span>
                )}
              </div>
              {workshop.organizer?.professionalRole && (
                <span className="wsp-organizer-role">{workshop.organizer.professionalRole}</span>
              )}
              {workshop.coOrganizers?.length > 0 && (
                <span className="wsp-co-organizers-text">
                  + {workshop.coOrganizers.slice(0, 2).map((co, idx) => (
                    <span key={co.user?._id || idx}>
                      {co.user?.name || "Unknown"}
                      {idx < Math.min(workshop.coOrganizers.length - 1, 1) && ", "}
                    </span>
                  ))}
                  {workshop.coOrganizers.length > 2 && ` +${workshop.coOrganizers.length - 2}`}
                </span>
              )}
            </div>
          </div>
          {renderParticipantPreview()}
        </div>
      </div>

      {/* Footer Action */}
      <div className="wsp-card-footer">{renderActionButton()}</div>
    </motion.article>
  );
};

export default WSPWorkshopCard;
